/**
 * LocalStorage and SessionStorage Key Constants
 * Centralized storage keys to prevent typos and ensure consistency
 */
export const STORAGE_KEYS = {
  // Authentication
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refreshToken',
  TOKEN_CREATION_DATE: 'tokenCreationDate',
  IS_SSO_AUTHENTICATED: 'isSSOAuthenticated',
  
  // User Information
  USER_EMAIL: 'userEmail',
  USER_NAME: 'userName',
  USER_ID: 'userId',
  USER_DETAILS: 'userDetails',
  USER_ROLE: 'userRole',
  
  // Session Management
  SESSION_ID: 'sessionId',
  LAST_ACTIVITY: 'lastActivity',
  SESSION_TIMEOUT: 'sessionTimeout',
  
  // Application State
  THEME: 'theme',
  LANGUAGE: 'language',
  SELECTED_TERRITORY: 'selectedTerritory',
  SELECTED_DIVISION: 'selectedDivision',
  
  // Redux Persist
  PERSIST_ROOT: 'persist:root',
  
  // Feature-specific
  DASHBOARD_FILTERS: 'dashboardFilters',
  TOUR_PLAN_DRAFT: 'tourPlanDraft',
  OFFLINE_QUEUE: 'offlineQueue',
  CACHED_DATA: 'cachedData',
  
  // App Settings
  APP_VERSION: 'appVersion',
  LAST_SYNC: 'lastSync',
  NOTIFICATION_PREFERENCES: 'notificationPreferences',
};

/**
 * Safe localStorage getter with error handling
 * @param {string} key - Storage key
 * @param {*} defaultValue - Default value if key doesn't exist
 * @returns {*} Parsed value or defaultValue
 */
export const getStorageItem = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    if (item === null) return defaultValue;
    
    // Try to parse JSON, return as-is if parsing fails
    try {
      return JSON.parse(item);
    } catch {
      return item;
    }
  } catch (error) {
    console.error(`Error reading from localStorage (${key}):`, error);
    return defaultValue;
  }
};

/**
 * Safe localStorage setter with error handling
 * @param {string} key - Storage key
 * @param {*} value - Value to store (will be JSON stringified)
 * @returns {boolean} Success status
 */
export const setStorageItem = (key, value) => {
  try {
    const serialized = typeof value === 'string' ? value : JSON.stringify(value);
    localStorage.setItem(key, serialized);
    return true;
  } catch (error) {
    console.error(`Error writing to localStorage (${key}):`, error);
    return false;
  }
};

/**
 * Remove item from localStorage
 * @param {string} key - Storage key
 * @returns {boolean} Success status
 */
export const removeStorageItem = (key) => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error(`Error removing from localStorage (${key}):`, error);
    return false;
  }
};

/**
 * Clear all localStorage except Redux persist
 * @param {boolean} keepPersist - Keep Redux persist data
 * @returns {boolean} Success status
 */
export const clearAllStorage = (keepPersist = true) => {
  try {
    if (keepPersist) {
      const persistData = localStorage.getItem(STORAGE_KEYS.PERSIST_ROOT);
      localStorage.clear();
      if (persistData) {
        localStorage.setItem(STORAGE_KEYS.PERSIST_ROOT, persistData);
      }
    } else {
      localStorage.clear();
    }
    return true;
  } catch (error) {
    console.error('Error clearing localStorage:', error);
    return false;
  }
};

/**
 * Check if a storage key exists
 * @param {string} key - Storage key
 * @returns {boolean} True if key exists
 */
export const hasStorageItem = (key) => {
  try {
    return localStorage.getItem(key) !== null;
  } catch (error) {
    console.error(`Error checking localStorage (${key}):`, error);
    return false;
  }
};

/**
 * Get cache key with timestamp
 * @param {string} key - Base key
 * @returns {string} Key with timestamp suffix
 */
export const getCacheKey = (key) => {
  return `${key}_${Date.now()}`;
};

/**
 * Check if cached data is expired
 * @param {string} key - Storage key
 * @param {number} maxAge - Max age in milliseconds
 * @returns {boolean} True if expired
 */
export const isCacheExpired = (key, maxAge) => {
  const timestamp = getStorageItem(`${key}_timestamp`);
  if (!timestamp) return true;
  
  return Date.now() - timestamp > maxAge;
};
